import { useState } from 'react';
import { IconEdit, IconTrash } from '../components/ActionIcons';
import { Pagination } from '../components/Pagination';
import {
  formatTanzaniaPhoneDisplay,
  normalizeTanzaniaMobileDigits,
  useStore,
} from '../context/StoreContext';
import { usePagination } from '../hooks/usePagination';
import { swalSuccess, swalError, swalConfirmDelete } from '../utils/swal';

const ROLES = ['admin', 'manager', 'cashier'];

export function UserManagement() {
  const { users, addUser, updateUser, deleteUser } = useStore();
  const {
    page,
    setPage,
    totalPages,
    slice: pageUsers,
    total,
    from,
    to,
  } = usePagination(users);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    role: 'cashier',
  });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [formError, setFormError] = useState(null);

  function resetAddForm() {
    setForm({ name: '', phone: '', role: 'cashier' });
    setFormError(null);
  }

  function closeAddModal() {
    setAddOpen(false);
    resetAddForm();
  }

  function handleAddSubmit(e) {
    e.preventDefault();
    setFormError(null);
    if (!form.name.trim() || !form.phone.trim()) {
      const msg = 'Name and phone are required.';
      setFormError(msg);
      swalError('Missing fields', msg);
      return;
    }
    const digits = normalizeTanzaniaMobileDigits(form.phone);
    if (!digits) {
      const msg =
        'Phone must start with +2556 or +2557 (Tanzania mobile, e.g. +255712345678).';
      setFormError(msg);
      swalError('Invalid phone', msg);
      return;
    }
    addUser({
      name: form.name.trim(),
      phone: digits,
      role: form.role,
    });
    closeAddModal();
    swalSuccess('User added', `${form.name.trim()} was added to the roster.`);
  }

  function startEdit(u) {
    setFormError(null);
    setEditingId(u.id);
    setEditDraft({
      name: u.name,
      phone: u.phone ?? '',
      role: u.role,
      active: u.active,
    });
  }

  function saveEdit() {
    if (!editingId) return;
    const digits = normalizeTanzaniaMobileDigits(editDraft.phone);
    if (!digits) {
      const msg =
        'Phone must start with +2556 or +2557 (Tanzania mobile, e.g. +255712345678).';
      setFormError(msg);
      swalError('Invalid phone', msg);
      return;
    }
    setFormError(null);
    updateUser(editingId, {
      name: editDraft.name.trim(),
      phone: digits,
      role: editDraft.role,
      active: editDraft.active,
    });
    setEditingId(null);
    swalSuccess('User updated', 'Changes were saved.');
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">User Management</h1>
        <p className="page-subtitle">
          Phone numbers must be Tanzania mobiles starting with +2556 or +2557
          (e.g. +255712345678).
        </p>
      </header>

      {addOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={closeAddModal}
        >
          <div
            className="modal"
            role="dialog"
            aria-labelledby="add-user-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="add-user-title" className="modal-title">
                New user
              </h2>
              <button
                type="button"
                className="modal-close"
                onClick={closeAddModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form className="modal-body" onSubmit={handleAddSubmit}>
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                  autoFocus
                />
              </label>
              <label className="field">
                <span className="field-label">Phone number</span>
                <input
                  className="input"
                  type="tel"
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, phone: e.target.value }))
                  }
                  placeholder="+255712345678"
                  required
                  autoComplete="tel"
                />
              </label>
              <label className="field">
                <span className="field-label">Role</span>
                <select
                  className="input"
                  value={form.role}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, role: e.target.value }))
                  }
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              {formError && (
                <p className="login-error" role="alert">
                  {formError}
                </p>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={closeAddModal}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save user
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <section className="card">
        <div className="card-toolbar">
          <h2 className="card-title">User list</h2>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setAddOpen(true)}
          >
            Add user
          </button>
        </div>
        {formError && !addOpen && editingId && (
          <p className="banner banner-error" role="alert">
            {formError}
          </p>
        )}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pageUsers.map((u) => (
                <tr key={u.id}>
                  {editingId === u.id ? (
                    <>
                      <td>
                        <input
                          className="input input--inline"
                          value={editDraft.name}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, name: e.target.value }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input--inline"
                          type="tel"
                          inputMode="tel"
                          value={editDraft.phone}
                          onChange={(e) =>
                            setEditDraft((d) => ({
                              ...d,
                              phone: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td>
                        <select
                          className="input input--inline"
                          value={editDraft.role}
                          onChange={(e) =>
                            setEditDraft((d) => ({
                              ...d,
                              role: e.target.value,
                            }))
                          }
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <label className="checkbox-inline">
                          <input
                            type="checkbox"
                            checked={editDraft.active}
                            onChange={(e) =>
                              setEditDraft((d) => ({
                                ...d,
                                active: e.target.checked,
                              }))
                            }
                          />
                          Active
                        </label>
                      </td>
                      <td className="table-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={saveEdit}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          onClick={() => {
                            setEditingId(null);
                            setFormError(null);
                          }}
                        >
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{u.name}</td>
                      <td className="mono">
                        {u.phone
                          ? formatTanzaniaPhoneDisplay(u.phone)
                          : '—'}
                      </td>
                      <td>
                        <span className="badge badge-role">{u.role}</span>
                      </td>
                      <td>
                        <span
                          className={
                            u.active ? 'badge badge-ok' : 'badge badge-muted'
                          }
                        >
                          {u.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="table-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost btn-icon"
                          onClick={() => startEdit(u)}
                          aria-label="Edit user"
                          title="Edit"
                        >
                          <IconEdit className="action-icon" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger btn-icon"
                          onClick={async () => {
                            const ok = await swalConfirmDelete({
                              title: 'Remove user?',
                              text: `Remove ${u.name} from the team?`,
                            });
                            if (!ok) return;
                            deleteUser(u.id);
                            swalSuccess('User removed', `${u.name} was deleted.`);
                          }}
                          aria-label="Delete user"
                          title="Delete"
                        >
                          <IconTrash className="action-icon" />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {users.length === 0 && (
          <p className="empty-hint">
            No users in the roster yet. Add staff with the button above.
          </p>
        )}
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          total={total}
          from={from}
          to={to}
        />
      </section>
    </div>
  );
}
